import { SquashLowRes } from '@src/components/SquashLowRes';
import { Squash, SquashMood } from '@src/data/types';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, HStack, Text, VStack } from 'ui_2';
import { BiBadgeCheck, BiChevronLeft } from 'react-icons/bi';
import { MuteButton } from '@src/components/MuteButton';
import { SelectedAccount } from '@src/components/SelectedAccount';
import { Route as game_route } from '../game';
import { useSquashGameState } from '@src/providers/squashGameState';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { router } from '@src/providers/router';

dayjs.extend(localizedFormat);

export const Route = createFileRoute('/game/completedSquash')({
  component: RouteComponent,
});

const SquashSelect = ({ squash }: { squash: Squash }) => {
  const level =
    squash.onChainLevel <= 5 ? squash.displayedLevel : squash.onChainLevel;
  const mood: SquashMood = squash.onChainLevel <= 5 ? squash.mood : 'happy';
  const completed = level >= 6;

  return (
    <VStack className='relative min-w-[150px] items-center rounded-xl bg-white p-4 shadow-lg transition-shadow duration-200 hover:shadow-xl'>
      {completed && (
        <BiBadgeCheck
          className='absolute -right-2 -top-2 rounded-full bg-green-500 p-1 text-2xl text-white shadow-md'
          size={40}
        />
      )}
      <div className='relative h-[80px] w-[80px]'>
        <SquashLowRes level={level} mood={mood} />
      </div>
      <VStack className='items-center'>
        <Text className='text-xl font-semibold text-gray-800'>
          {squash.squashName}
        </Text>
        <Text className='text-gray-600'>
          {dayjs(squash.lastWatered).format('L')}
        </Text>
      </VStack>
    </VStack>
  );
};

function RouteComponent() {
  const { inactiveSquashes, account } = useSquashGameState();

  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <VStack className='h-[100dvh] gap-4 overflow-y-scroll p-4 pb-16'>
      <Button
        variant='icon'
        className='bg-transparent'
        size='icon'
        onClick={() => {
          if (router.history.canGoBack()) {
            router.history.back();
          } else {
            navigate({ to: game_route.to, reloadDocument: true });
          }
        }}
      >
        <BiChevronLeft />
      </Button>
      <HStack className='mt-4 w-full'>
        <VStack>
          <Text
            font='bold-lg'
            family='atak'
            className='[-webkit-text-stroke:2px_white] [paint-order:stroke_fill] [stroke-width:2px] [stroke:2px_white] [text-stroke:2px_white]'
          >
            Your Past Squashes
          </Text>
        </VStack>
        <HStack className='flex-grow' />
        <VStack className='items-end gap-2'>
          {account && <SelectedAccount account={account} />}
          <HStack className='items-center gap-2'>
            <MuteButton />
          </HStack>
        </VStack>
      </HStack>
      {inactiveSquashes && inactiveSquashes.length > 0 && (
        <div className='grid grid-cols-2 gap-4 p-4'>
          {inactiveSquashes.map((squash, ix) => (
            <SquashSelect squash={squash} key={ix} />
          ))}
        </div>
      )}
      {inactiveSquashes && inactiveSquashes.length === 0 && (
        <Text>You don't have any completed squashes!</Text>
      )}
    </VStack>
  );
}
